import { useState, FC, useRef, useEffect } from 'react';
import { Formik, useFormikContext } from 'formik';
import { ComputeEntitySelector } from 'modules/Compute/Components/ComputeEntitySelector/ComputeEntitySelector';
import { useRouter } from 'next/router';
import { useFeatureFlagsLD } from 'modules/FeatureFlagsLD/FeatureFlagsProviderLD';
import { useCurrentUserId } from 'modules/Auth/hooks';
import { PlaygroundConfig } from 'modules/Playground/types/playgroundConfig';
import { css } from '@linaria/core';
import { TabButtons } from 'modules/Playground/Components/TabButtons/TabButtons';
import { StreamingToggle } from 'modules/Playground/Components/StreamingToggle/StreamingToggle';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { formatPricingPerHour } from 'utils/pricing';
import { breakpoints } from 'styles/utils';
import { ModelPricing } from 'components/Pricing/ModelPricing';
import SvgSliders04Md from 'modules/Playground/Components/Icons/Sliders04Md';
import { MobileSidebarHandle, PlaygroundChatMobileConfigSidebar } from 'modules/Playground/Components/PlaygroundChatMobileConfigSidebar';
import { useGenerateOptions } from 'modules/Compute/Components/ComputeEntitySelector/useGenerateOptions';
import { useCompareMode } from 'modules/Playground/hooks/utils/useCompareMode';
import { ChatConfigInCompareMode } from '../ChatConfigInCompareMode';
interface PlaygroundContentHeaderProps {
  hideStreamingPlaygroundLDFF?: boolean;
}
export const PlaygroundContentHeader: FC<PlaygroundContentHeaderProps> = ({
  hideStreamingPlaygroundLDFF
}) => {
  const {
    track
  } = useDataCollection();
  const {
    values,
    setFieldValue
  } = useFormikContext<PlaygroundConfig>();
  const {
    isCompareMode
  } = useCompareMode();
  const handleDeploymentChange = (selectedDeploymentId: string, option: Record<string, unknown>) => {
    setFieldValue('deploymentId', selectedDeploymentId);
    const {
      rawData
    } = (option as Record<'rawData', CF.API.Compute.Deployment>);
    const price = rawData?.nodepools?.map(nodepool => nodepool.instance_types?.map(instanceType => instanceType.price)).flat()?.[0];
    setFieldValue('deploymentPrice', price !== undefined ? `$ ${formatPricingPerHour(Number(price ?? 0))} / h / instance` : null);
  };
  const sidebarRef = useRef<MobileSidebarHandle>(null);
  const openSidebar = () => {
    sidebarRef.current?.openDialog();
  };
  const router = useRouter();
  const isStreamEnabled = router.query.streaming !== 'false';
  const [streamingEnabled, setStreamingEnabled] = useState(isStreamEnabled);
  const {
    streamingPlaygroundLDFF
  } = useFeatureFlagsLD();

  // Sync the isStreamEnabled state with the Formik field value
  useEffect(() => {
    setFieldValue('isStreaming', isStreamEnabled);
  }, [isStreamEnabled]);
  const currentUserId = useCurrentUserId();
  const modelVersionId = values.modelVersion?.id ?? values.model?.model_version?.id ?? undefined;
  const handleStreamingChange = () => {
    setStreamingEnabled(oldStreamingValue => {
      const newStreamingValue = !oldStreamingValue;
      // Update query param without reloading page...
      track('Playground -> Streaming Mode Toggled', {
        userId: currentUserId,
        modelId: values.model?.id,
        streaming: newStreamingValue
      });
      router.push({
        query: {
          ...router.query,
          streaming: newStreamingValue.toString()
        }
      }, undefined, {
        shallow: true
      });
      return newStreamingValue;
    });
  };
  const deploymentOptions = useGenerateOptions({
    entityType: 'deployment',
    model: values.model,
    modelVersionIds: modelVersionId ? [modelVersionId] : []
  });
  return <div className={styles.playgroundContentHeader}>
      <div className={styles.playgroundContentTabSection}>
        <TabButtons />
        {!isCompareMode && <StreamingToggle hide={!streamingPlaygroundLDFF || hideStreamingPlaygroundLDFF} isEnabled={streamingEnabled} onChange={handleStreamingChange} tooltipText={streamingTooltip} />}
      </div>

      <div className={styles.playgroundContentHeaderInfoSection}>
        {values.model && !isCompareMode && <ModelPricing border={false} model={((values.model as unknown) as CF.API.Models.Model)} />}
        {/* <InfoPrice price={priceText} hide={isHidden} /> */}
        {/* TODO: This formik wrapper should be avoided - must change underlying compute entity selector component */}
        {/* TODO: Rebuild the compute entity selector, dumb component with smart hooks */}
        <div className={styles.playgroundDeploySelectorWrapper}>
          <Formik initialValues={{}} onSubmit={() => {}}>
            {modelVersionId && <ComputeEntitySelector className={styles.playgroundDeploySelector} data-testid="deploy-cluster-id" entityType="deployment" name="deploymentId" onSelect={handleDeploymentChange} options={deploymentOptions} />}
          </Formik>
        </div>

        {!isCompareMode &&
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div onClick={openSidebar} className={styles.playgroundMobileSettingsConfig}>
            <SvgSliders04Md />
          </div>}
        {isCompareMode && <ChatConfigInCompareMode />}
      </div>

      <PlaygroundChatMobileConfigSidebar ref={sidebarRef} deploymentOptions={deploymentOptions} />
    </div>;
};
export const styles = {
  playgroundContentHeader: "odules__Playground__Components__PlaygroundContentHeader__PlaygroundContentHeader_tsx-playgroundContentHeader-p11yw7ik",
  playgroundContentTabSection: "odules__Playground__Components__PlaygroundContentHeader__PlaygroundContentHeader_tsx-playgroundContentTabSection-p1btlrox",
  playgroundDeploySelectorWrapper: "odules__Playground__Components__PlaygroundContentHeader__PlaygroundContentHeader_tsx-playgroundDeploySelectorWrapper-p2gcuse",
  playgroundMobileSettingsConfig: "odules__Playground__Components__PlaygroundContentHeader__PlaygroundContentHeader_tsx-playgroundMobileSettingsConfig-p1k0uj3a",
  playgroundDeploySelector: "odules__Playground__Components__PlaygroundContentHeader__PlaygroundContentHeader_tsx-playgroundDeploySelector-pp9s1me",
  playgroundContentHeaderInfoSection: "odules__Playground__Components__PlaygroundContentHeader__PlaygroundContentHeader_tsx-playgroundContentHeaderInfoSection-p1o6f68z",
  streamingTooltipStyles: "odules__Playground__Components__PlaygroundContentHeader__PlaygroundContentHeader_tsx-streamingTooltipStyles-s1yahvja"
};
const streamingTooltip = <div className={styles.streamingTooltipStyles}>
    <span>Streaming Mode:</span>
    <p>Toggle model streaming, this toggle is currently only visible to Clarifai internal users.</p>
  </div>;

require("./PlaygroundContentHeader.linaria.module.css!=!../../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./PlaygroundContentHeader.tsx");